// Pay-an-invoice dialog used by both the User detail page and the Pending
// Invoices page. Lets the super admin settle an UNPAID subscription invoice
// via:
//   - Cash          (no charge, records the settlement)
//   - Check         (no charge, requires check_number)
//   - Card          (Stripe charge; optional Save-this-card)
//
// For the card mode a SetupIntent is fetched against the invoice's owner
// (POST /admin/users/:id/payment-methods/setup-intent), and a single
// <Elements> wrapper encloses BOTH the PaymentElement form and the submit
// button so the Stripe hooks resolve to the same instance.
//
// Submitting posts to /admin/invoices/:id/pay; the backend marks the
// invoice PAID, records paid_via / check_number / paid_by_admin_email, and
// runs the subscription reset (same downstream as customer resubscribe).
import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
    Box, ToggleButton, ToggleButtonGroup, TextField, Alert, Checkbox,
    FormControlLabel, CircularProgress, Stack, Divider,
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import axiosInstance from '../utils/axiosInstance';
import { stripe_publishable_key } from '../config';
import { formatCurrency } from '../utils/format';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const stripePromise = stripe_publishable_key ? loadStripe(stripe_publishable_key) : null;

// Method picker (cash / check / card).
const MethodPicker = ({ method, setMethod, disabled }) => (
    <ToggleButtonGroup
        exclusive value={method} disabled={disabled}
        onChange={(_, v) => v && setMethod(v)}
        fullWidth size="small" sx={{ mb: 2 }}
    >
        <ToggleButton value="cash" sx={{ fontFamily, textTransform: 'none' }}>
            <PaymentsIcon fontSize="small" sx={{ mr: 0.75 }} /> Cash
        </ToggleButton>
        <ToggleButton value="check" sx={{ fontFamily, textTransform: 'none' }}>
            <ReceiptIcon fontSize="small" sx={{ mr: 0.75 }} /> Check
        </ToggleButton>
        <ToggleButton value="card" sx={{ fontFamily, textTransform: 'none' }}>
            <CreditCardIcon fontSize="small" sx={{ mr: 0.75 }} /> Card
        </ToggleButton>
    </ToggleButtonGroup>
);

const InvoiceSummary = ({ invoice }) => (
    <Box sx={{
        backgroundColor: '#f8fafc', border: '1px solid #e2e8f0',
        borderRadius: '10px', p: 2, mb: 2,
    }}>
        <Stack direction="row" justifyContent="space-between">
            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                <strong>Customer:</strong> {invoice.billed_to_email || `#${invoice.team_owner_id}`}
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                <strong>Plan:</strong> {invoice.plan_name || '--'}
            </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#64748b' }}>
                <strong>Period:</strong> {invoice.period_label || '--'}
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>
                {formatCurrency(invoice.total, invoice.currency)}
            </Typography>
        </Stack>
    </Box>
);

const FooterNote = () => (
    <>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontFamily, fontSize: '0.78rem', color: '#94a3b8' }}>
            Settling this invoice marks it PAID, runs the standard subscription reset
            (status → Active, fresh billing period, usage refilled) and logs a history row.
        </Typography>
    </>
);

// =============== Card mode (renders inside <Elements>) ====================

const CardModeInner = ({
    invoice, saveCard, setSaveCard,
    busy, setBusy, setError, setInfo,
    error, info, onPaid, onCancel,
    method, setMethod, checkNumber, setCheckNumber,
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleCharge = async () => {
        if (busy) return;
        setError(null); setInfo(null);
        if (!stripe || !elements) {
            setError('The payment form is still loading. Please wait a moment.');
            return;
        }
        setBusy(true);
        try {
            setInfo('Securing card details...');
            const { error: stripeError, setupIntent } = await stripe.confirmSetup({
                elements, confirmParams: {}, redirect: 'if_required',
            });
            if (stripeError) {
                setError(stripeError.message || 'Please check the card details.');
                setInfo(null); return;
            }
            const pmId = setupIntent?.payment_method;
            if (!pmId) { setError('Could not read the card.'); setInfo(null); return; }
            setInfo('Charging card and resetting subscription...');
            const r = await axiosInstance.post(`/admin/invoices/${invoice.id}/pay`, {
                method: 'card',
                gateway_payment_method_id: pmId,
                save_card: !!saveCard,
            });
            setInfo(null);
            onPaid?.(r.data?.data || null);
        } catch (err) {
            setInfo(null);
            setError(err.response?.data?.message || err.message || 'Card charge failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <DialogContent dividers>
                <InvoiceSummary invoice={invoice} />
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 1 }}>
                    Payment method
                </Typography>
                <MethodPicker method={method} setMethod={setMethod} disabled={busy} />
                <Box sx={{ mt: 1 }}>
                    <PaymentElement options={{ layout: 'tabs' }} />
                    <FormControlLabel
                        sx={{ mt: 1.5 }}
                        control={
                            <Checkbox
                                checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)}
                                size="small"
                            />
                        }
                        label={(
                            <Typography sx={{ fontFamily, fontSize: '0.875rem', color: '#475569' }}>
                                Save this card on the user's account for future use
                            </Typography>
                        )}
                    />
                </Box>
                {error && <Alert severity="error" sx={{ fontFamily, mt: 2 }}>{error}</Alert>}
                {info && <Alert severity="info" sx={{ fontFamily, mt: 2 }}>{info}</Alert>}
                <FooterNote />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={busy} sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained" onClick={handleCharge}
                    disabled={busy || !stripe || !elements}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                >
                    {busy ? 'Processing...' : `Charge ${formatCurrency(invoice.total, invoice.currency)}`}
                </Button>
            </DialogActions>
        </>
    );
};

// =============== Cash / Check mode (no Stripe needed) =====================

const NonCardMode = ({
    invoice, method, setMethod, checkNumber, setCheckNumber,
    busy, setBusy, setError, setInfo, error, info, onPaid, onCancel,
}) => {
    const submit = async () => {
        if (busy) return;
        if (method === 'check' && !checkNumber.trim()) {
            setError('Check number is required.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const r = await axiosInstance.post(`/admin/invoices/${invoice.id}/pay`, {
                method,
                check_number: method === 'check' ? checkNumber.trim() : undefined,
            });
            onPaid?.(r.data?.data || null);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Settlement failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <DialogContent dividers>
                <InvoiceSummary invoice={invoice} />
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 1 }}>
                    Payment method
                </Typography>
                <MethodPicker method={method} setMethod={setMethod} disabled={busy} />
                {method === 'cash' && (
                    <Alert severity="info" sx={{ fontFamily, mb: 2 }}>
                        Records a cash settlement and resets this user's subscription.
                        No charge will be made.
                    </Alert>
                )}
                {method === 'check' && (
                    <>
                        <Alert severity="info" sx={{ fontFamily, mb: 2 }}>
                            Records a check settlement and resets this user's subscription.
                            No charge will be made.
                        </Alert>
                        <TextField
                            fullWidth size="small" label="Check number"
                            value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)}
                            disabled={busy} sx={{ mb: 2 }}
                        />
                    </>
                )}
                {error && <Alert severity="error" sx={{ fontFamily, mt: 1 }}>{error}</Alert>}
                {info && <Alert severity="info" sx={{ fontFamily, mt: 1 }}>{info}</Alert>}
                <FooterNote />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={busy} sx={{ fontFamily, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained" onClick={submit} disabled={busy}
                    startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 700 }}
                >
                    {busy
                        ? 'Processing...'
                        : `Settle ${formatCurrency(invoice.total, invoice.currency)}`}
                </Button>
            </DialogActions>
        </>
    );
};

// =============== Outer dialog ============================================

const PayInvoiceDialog = ({ open, invoice, onClose, onPaid }) => {
    const [method, setMethod] = useState('cash');
    const [checkNumber, setCheckNumber] = useState('');
    const [saveCard, setSaveCard] = useState(true);
    const [clientSecret, setClientSecret] = useState(null);
    const [intentLoading, setIntentLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    // Reset state on (re)open / invoice change.
    useEffect(() => {
        if (!open) return;
        setMethod('cash'); setCheckNumber(''); setSaveCard(true);
        setClientSecret(null); setError(null); setInfo(null); setBusy(false);
    }, [open, invoice?.id]);

    // Fetch SetupIntent the first time the admin switches to Card.
    useEffect(() => {
        const needSecret = open && method === 'card' && !clientSecret && invoice?.team_owner_id;
        if (!needSecret) return;
        let cancelled = false;
        (async () => {
            setIntentLoading(true);
            setError(null);
            try {
                const r = await axiosInstance.post(
                    `/admin/users/${invoice.team_owner_id}/payment-methods/setup-intent`,
                    {},
                );
                if (cancelled) return;
                setClientSecret(r.data?.client_secret || null);
                if (!r.data?.client_secret) setError('Could not initialise the card form.');
            } catch (err) {
                if (cancelled) return;
                setError(err.response?.data?.message || 'Could not initialise the card form.');
            } finally {
                if (!cancelled) setIntentLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, method, clientSecret, invoice?.team_owner_id]);

    const stripeOptions = useMemo(
        () => (clientSecret ? { clientSecret, appearance: { theme: 'stripe' } } : null),
        [clientSecret],
    );

    if (!invoice) return null;

    // When card mode is selected, choose Elements vs a loading/error placeholder.
    let cardBody = null;
    if (method === 'card') {
        if (intentLoading) {
            cardBody = (
                <>
                    <DialogContent dividers>
                        <InvoiceSummary invoice={invoice} />
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', mb: 1 }}>
                            Payment method
                        </Typography>
                        <MethodPicker method={method} setMethod={setMethod} disabled />
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    </DialogActions>
                </>
            );
        } else if (!stripePromise) {
            cardBody = (
                <>
                    <DialogContent dividers>
                        <InvoiceSummary invoice={invoice} />
                        <MethodPicker method={method} setMethod={setMethod} disabled={busy} />
                        <Alert severity="error" sx={{ fontFamily }}>
                            Stripe publishable key is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY
                            in klonit-admin/react-app/.env.
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                    </DialogActions>
                </>
            );
        } else if (!clientSecret) {
            cardBody = (
                <>
                    <DialogContent dividers>
                        <InvoiceSummary invoice={invoice} />
                        <MethodPicker method={method} setMethod={setMethod} disabled={busy} />
                        <Alert severity="warning" sx={{ fontFamily }}>
                            {error || 'Could not load the Stripe form for this user. Try cash or check.'}
                        </Alert>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none' }}>Close</Button>
                    </DialogActions>
                </>
            );
        } else {
            cardBody = (
                <Elements stripe={stripePromise} options={stripeOptions}>
                    <CardModeInner
                        invoice={invoice}
                        saveCard={saveCard} setSaveCard={setSaveCard}
                        busy={busy} setBusy={setBusy}
                        setError={setError} setInfo={setInfo}
                        error={error} info={info}
                        onPaid={onPaid} onCancel={onClose}
                        method={method} setMethod={setMethod}
                        checkNumber={checkNumber} setCheckNumber={setCheckNumber}
                    />
                </Elements>
            );
        }
    }

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                Pay invoice {invoice.invoice_number}
            </DialogTitle>
            {method === 'card' ? cardBody : (
                <NonCardMode
                    invoice={invoice} method={method} setMethod={setMethod}
                    checkNumber={checkNumber} setCheckNumber={setCheckNumber}
                    busy={busy} setBusy={setBusy}
                    setError={setError} setInfo={setInfo}
                    error={error} info={info}
                    onPaid={onPaid} onCancel={onClose}
                />
            )}
        </Dialog>
    );
};

export default PayInvoiceDialog;
